import { ShoppingCartItemItxDto, ShoppingCartItxDto } from "src/api/customer";
import { useShop } from "src/hooks/Utils/Shop/useShop";
import { ApiError } from "src/model/api-error.model";
import appState from "src/model/state";
import { checkUserLogged } from "src/model/utils/api-response";
import { OpenapiSingleton } from "src/utils/OpenapiSingleton";
import { RESELL_GUEST_CART_CODE } from "src/utils/constants";
import { preparePaymentStage, prepareShippingStage } from "./checkout.service";
import { forceLogin } from "./profile.service";


const TS_CACHE = 500000;

  export const getShoppingCart = async (forceRefresh?: boolean) => {
    const { cart: cartState } = appState.get();
    if (!forceRefresh && cartState.content && cartState.lastLoad > (Date.now() - TS_CACHE)) {
        return;
    }
    
    const {shopMarketId, shopLang} = useShop();
    const user = appState.get().profile.user;
    
    
    const cartCode = localStorage.getItem(RESELL_GUEST_CART_CODE);
    let cart: ShoppingCartItxDto;
    try {
        if (cartCode) {
            const response = await OpenapiSingleton.BuyerShoppingCartAPIApi.getProductToCart(cartCode, shopMarketId, shopLang);
            cart = response?.data;
        }
        else {
            if (user) {
                const response = await OpenapiSingleton.BuyerShoppingCartAPIApi.getCurrentCartV2(shopMarketId, shopLang);
                cart = response?.data;
            }
            else return null;
        }

        if (cart?.shoppingCartCode !== null) {
            appState.execute("CART_SET", cart);
        } else {
            appState.execute("CART_UNSET", undefined);
        }
    } catch (e: any) {
        const error = await e?.response?.data as ApiError;
        if (error?.parameter === "loggedUser") {
            localStorage.removeItem(RESELL_GUEST_CART_CODE);
            appState.execute("CART_UNSET", undefined);
        } else if (error?.parameter === "LoggedCustomer") {
            forceLogin();
        } else if (error?.parameter === "CurrentCart") { // no current cart
            appState.execute("CART_UNSET", undefined);
        }
    }

    if (user && cart) {
        localStorage.removeItem(RESELL_GUEST_CART_CODE);
    }
}

export const setEmptyCart = () => {
    appState.execute("CART_UNSET", undefined);
    localStorage.removeItem(RESELL_GUEST_CART_CODE);
}

export const addToCart = async (productId: number) => {
    appState.execute("CART_ADD", productId);
    const {shopMarketId, shopLang, shopId} = useShop()
    const {  cart: cartState, profile } = appState.get();
    let cart = cartState.content;
    const cartCode = localStorage.getItem(RESELL_GUEST_CART_CODE);
    if (!cart && cartCode) {
        // in this case we fetch the cart. If the user is logged in, the guest cart
        // will be the user's cart. If it's not, we use the guest cart to add items
        // to it
        await getShoppingCart(true);
        cart = appState.get().cart.content;
    }

    const user = profile.user;
    const guestUserId = profile?.userFromCookies?.user_type?.toLowerCase() === "guest" ? Number(profile?.userFromCookies?.user_id) : undefined;

    try {
        if (cart) {
            const response = await OpenapiSingleton.BuyerShoppingCartAPIApi.addProductToCart( 
                cart.shoppingCartCode,
                productId,
                shopMarketId,
                shopLang,
            );
            cart = response?.data
        } else {
            const response = await OpenapiSingleton.BuyerShoppingCartAPIApi.create1(
                productId,
                shopMarketId, 
                shopLang,
                guestUserId,
                shopId?.toString(),
            );
            cart = response?.data
            if (!user) {
                localStorage.setItem(RESELL_GUEST_CART_CODE, cart?.shoppingCartCode);
            }
        }
        appState.execute("CART_UPDATED", cart);
        return cart;
    } catch (e: any) {
        if (e.response) {
            const error = e;
            const value = await error?.response?.data as ApiError;
            checkUserLogged(e);
            if (value.title === 'already-in-cart') {
                appState.execute("CART_UPDATE_ERROR", {
                    titleKey: "ADD_TO_CART",
                    messageKey: "PRODUCT_IN_CART"
                });
            }
            return null;
        }
        appState.execute("CART_UPDATE_ERROR", {
            messageKey: "ADD_TO_CART",
            titleKey: "ADD_TO_CART"
        });
        return null;
    }
}

export const dismissUpdateError = () => appState.execute("CART_UPDATE_DISMISS_ERROR", undefined);

export const removeFromCart = async (productIds: number[], successActionCallback?: () => void, withoutProductsCallback?: () => void) => {
    appState.execute("CHK_SET_LOADING", true);
    for (const id of productIds) { 
        appState.execute("CART_REMOVE", id);
    }
    const { cart: cartState } = appState.get();
    let cart = cartState.content;
    const { shopMarketId } = useShop()
    if (!cart) {
        appState.execute("CHK_SET_LOADING", false);
        return; // we only can remove items if we have a cart
    }
  
    try {
        const response = await OpenapiSingleton.BuyerShoppingCartAPIApi.deleteProductListFromCart(
            cart.shoppingCartCode,
            shopMarketId,
            productIds,
        );
        cart = response?.data
        appState.execute("CART_UPDATED", cart);

        if (cart?.itemCount !== 0 && successActionCallback) {
            if (location?.pathname?.includes("shipping")) {
                prepareShippingStage();
            } else if (location?.pathname?.includes("payment")) {
                preparePaymentStage();
                appState.execute("CART_SET_PAYMENT_ELEMENT_EXPANDED", true);
            }
            successActionCallback();
        } else {
            withoutProductsCallback();
        }
    } catch (e: any) {
        if (e?.response?.data) {
            checkUserLogged(e);
        }
        appState.execute("CART_UPDATE_ERROR", {
            messageKey: "REMOVE_FROM_CART",
            titleKey: "REMOVE_FROM_CART"
        });
    } finally {
        appState.execute("CHK_SET_LOADING", false);
    }
}

export const acceptItemChanges = async (item: ShoppingCartItemItxDto) => {
    appState.execute("CART_CHECK_PRODUCT", item.productItx.id);
    const { shopMarketId, shopLang } = useShop()
    let cart = appState.get().cart.content;
    try {
        const response = await OpenapiSingleton.BuyerShoppingCartAPIApi.acceptCartChanges1(
            cart.shoppingCartCode,
            item.productItx.id,
            shopMarketId,
            shopLang
        );
        cart = response?.data
        appState.execute("CART_SET", cart);
        appState.execute("CART_CHECK_FINISHED", undefined);
    } catch (e: any) {
        if (e?.response?.data) {
            checkUserLogged(e);
        }
        appState.execute("CART_CHECK_PRODUCT_ERROR", {
            messageKey: "ACCEPT_CART_CHANGES",
            titleKey: "ACCEPT_CART_CHANGES"
        });
    }
}

export const dismissProductCheckError = 
    () => appState.execute("CART_CHECK_FINISHED", undefined);
